import React, { ChangeEvent } from 'react';
import {
	Grid,
	Card,
	CardHeader,
	CardContent,
	FormGroup,
	makeStyles,
	TextField,
	Select,
	MenuItem,
	InputLabel,
	FormControlLabel,
	Switch
} from '@material-ui/core';
import { Channel as ChannelType, VideoAsset, VideoAssetConnection } from '../../utils/types';
import VideoAssets from './VideoAssets';

const useStyles = makeStyles((theme) => ({
	inputField: {
		paddingBottom: theme.spacing(2)
	}
}));

export const ChannelDetailsEditor: React.FC<ChannelDetailsEditorProps> = (props) => {
	const { id, onChange, onSourceAssetsChange, name, language, territory, type, highlights, sourceAssets, mediaLiveId, mediaPackageId, idEditable } =
		props;
	const classes = useStyles();

	const handleSelectChange = (selectEvent: ChangeEvent<{ value: unknown }>): void => {
		const { name, value } = selectEvent.target as HTMLSelectElement;
		const event: SelectChangeEvent = {
			target: {
				name,
				value
			}
		};
		onChange && onChange(event);
	};

	const handleCheckBoxChange = (checkBoxEvent: ChangeEvent<{ name: string; checked: boolean }>): void => {
		const event: CheckBoxChangeEvent = {
			target: {
				name: checkBoxEvent.target.name,
				value: checkBoxEvent.target.checked
			}
		};
		onChange && onChange(event);
	};

	const handleVideoAssetAdd = (data: VideoAsset): Promise<void> => {
		const newAssets = sourceAssets?.items ? [...sourceAssets.items, data] : [data];
		onSourceAssetsChange({ items: newAssets });
		return Promise.resolve();
	};

	const handleVideoAssetDelete = (oldData: VideoAsset): Promise<void> => {
		const dataDelete = sourceAssets?.items ? [...sourceAssets.items] : [];
		const index = dataDelete.indexOf(oldData);
		dataDelete.splice(index, 1);
		onSourceAssetsChange({ items: [...dataDelete] });
		return Promise.resolve();
	};

	const handleVideoAssetUpdate = (newData: VideoAsset, oldData?: VideoAsset): Promise<void> => {
		if (oldData) {
			const dataUpdate = sourceAssets?.items ? [...sourceAssets.items] : [];
			const index = dataUpdate.indexOf(oldData);
			dataUpdate[index] = newData;
			onSourceAssetsChange({ items: [...dataUpdate] });
		}
		return Promise.resolve();
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} lg={6}>
				<Card>
					<CardHeader title='Channel details' />
					<CardContent>
						<FormGroup>
							<TextField
								id='id'
								label='ID'
								name='id'
								className={classes.inputField}
								value={id}
								onChange={onChange}
								disabled={!idEditable}
								fullWidth
								required
								inputProps={{
									'data-testid': 'channel-editor-id'
								}}
							/>
							<TextField
								id='name'
								label='Name'
								name='name'
								className={classes.inputField}
								value={name}
								onChange={onChange}
								fullWidth
								required
								inputProps={{
									'data-testid': 'channel-editor-name'
								}}
							/>
							<TextField
								id='mediaLiveId'
								label='Media Live ID'
								name='mediaLiveId'
								className={classes.inputField}
								value={mediaLiveId}
								onChange={onChange}
								fullWidth
								inputProps={{
									'data-testid': 'media-live-id'
								}}
							/>
							<TextField
								id='mediaPackageId'
								label='Media Package ID'
								name='mediaPackageId'
								className={classes.inputField}
								value={mediaPackageId}
								onChange={onChange}
								fullWidth
								inputProps={{
									'data-testid': 'media-package-id'
								}}
							/>
							<InputLabel id='language-label'>Language</InputLabel>
							<Select
								labelId='language-label'
								id='language-select'
								value={language}
								name='language'
								onChange={handleSelectChange}
								className={classes.inputField}
								fullWidth
								required
								inputProps={{
									'data-testid': 'channel-editor-language'
								}}>
								<MenuItem value='en'>en</MenuItem>
								<MenuItem value='de'>de</MenuItem>
								<MenuItem value='it'>it</MenuItem>
								<MenuItem value='fr'>fr</MenuItem>
								<MenuItem value='es'>es</MenuItem>
							</Select>
							<InputLabel id='territory-label'>Territory</InputLabel>
							<Select
								labelId='territory-label'
								id='territory-select'
								value={territory}
								name='territory'
								onChange={handleSelectChange}
								className={classes.inputField}
								fullWidth
								required
								inputProps={{
									'data-testid': 'channel-editor-territory'
								}}>
								<MenuItem value='GB'>GB</MenuItem>
								<MenuItem value='DE'>DE</MenuItem>
								<MenuItem value='IT'>IT</MenuItem>
								<MenuItem value='FR'>FR</MenuItem>
								<MenuItem value='AT'>AT</MenuItem>
								<MenuItem value='US'>US</MenuItem>
							</Select>
							<InputLabel id='type-label'>Type</InputLabel>
							<Select
								labelId='type-label'
								id='type-select'
								value={type}
								name='type'
								onChange={handleSelectChange}
								className={classes.inputField}
								fullWidth
								required
								inputProps={{
									'data-testid': 'channel-editor-type'
								}}>
								<MenuItem value='LIVE'>LIVE</MenuItem>
								<MenuItem value='TEST'>TEST</MenuItem>
								<MenuItem value='EXTERNAL'>EXTERNAL</MenuItem>
							</Select>
							<FormControlLabel
								control={
									<Switch
										name='highlights'
										checked={highlights ?? false}
										onChange={handleCheckBoxChange}
										color='primary'
										data-testid='channel-editor-has-highlights'
									/>
								}
								label='Highlights'
								labelPlacement='start'
							/>
						</FormGroup>
					</CardContent>
				</Card>
			</Grid>
			<Grid item xs={12} lg={12}>
				<VideoAssets
					videoAssets={sourceAssets?.items ?? []}
					onRowAdd={handleVideoAssetAdd}
					onRowDelete={handleVideoAssetDelete}
					onRowUpdate={handleVideoAssetUpdate}
				/>
			</Grid>
		</Grid>
	);
};

type ChannelDetailsEditorProps = ChannelType & {
	onChange: (event: ChangeEvent<HTMLInputElement> | SelectChangeEvent | CheckBoxChangeEvent) => void;
	onSourceAssetsChange: (assetConnection: VideoAssetConnection) => void;
	idEditable?: boolean;
};

export type CheckBoxChangeEvent = {
	target: {
		name: string;
		value: boolean;
	};
};

export type SelectChangeEvent = {
	target: {
		name: string;
		value: string;
	};
};

export default ChannelDetailsEditor;
